import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import EmpLayout from "./emp_layout";
import "./css/employer_dashboard.css"; 
import "./css/candidate_search.css";    

export default function CandidateSearch() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem("candidateSearchQuery") || "";
  });
  
  // const [searchQuery, setSearchQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) navigate("/signin");
  }, [token, navigate]);

  useEffect(() => {
    sessionStorage.setItem("candidateSearchQuery", searchQuery);
  }, [searchQuery]);

  // Handle live search with debouncing
  useEffect(() => {
    // If the user clears the input field, empty out the results immediately without calling the API
    if (!searchQuery.trim()) {
      setCandidates([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Set a timer to run the search after the user stops typing for 350ms
    const delayDebounceFn = setTimeout(async () => {
      try {
        const queryParams = new URLSearchParams({ query: searchQuery }).toString();
        const targetUrl = `${API_ENDPOINTS.EMPLOYERSEARCHTALENT}?${queryParams}`;
        
        const response = await fetch(targetUrl, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        if (!response.ok) throw new Error("Live search failed");
        const data = await response.json();
        setCandidates(data.candidates || []);
      } catch (err) {
        console.error("Live search error:", err);
      } finally {
        setLoading(false);
      }
    }, 350); // 350 milliseconds window

    // Clean up timer if the user types another character before the 350ms completes
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, token, navigate]);

  return (
    <EmpLayout title="Discover" titleEm="Talent ◈">
      <div className="emd-fade theme-search-container">
        
        {/* Streamlined Live Input Control Frame */}
        <div className="emd-post-card theme-search-header">
          <div className="theme-search-form">
            <div className="theme-search-input-wrapper">
              <span className="theme-search-icon">🔍</span>
              <input
                type="text"
                className="theme-search-input"
                placeholder="Type a name, skill, or job title to search instantly..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {loading && (
              <div className="theme-live-loader">
                <div className="si-spinner small-spinner"></div>
              </div>
            )}
          </div>
        </div>

        {/* Results Matrix Block */}
        <div className="emd-panel-card theme-results-panel">
          <div className="emd-section-hdr" style={{ padding: "0.5rem 1rem 0" }}>
            <div className="emd-section-title">
              {searchQuery.trim() ? `Discovered Profiles (${candidates.length})` : "Type to see candidates"}
            </div>
          </div>

          {candidates.length > 0 ? (
            <div className="theme-feed-list">
              {candidates.map((cand) => {
                const initials = cand.name 
                  ? cand.name.trim().split(/\s+/).map(n => n[0]).join("").toUpperCase()
                  : "??";

                return (
                  <div 
                    key={cand.id} 
                    className="theme-candidate-row" 
                    onClick={() => navigate(`/employer/candidate-search-profile/${cand.id}`)}
                  >
                    {/* <div className="theme-row-avatar">{initials}</div> */}

                    <div className="theme-row-info">
                      <div className="theme-row-top-line">
                        <span className="theme-row-name">{cand.name}</span>
                        <span className="theme-row-degree">• 3rd+</span>
                      </div>
                      <div className="theme-row-headline">
                        {cand.headline || `${cand.specialisation || "Professional"} · ${cand.experience || "No specified experience"}`}
                      </div>
                      <div className="theme-row-location">📍 {cand.location || "Flexible / Remote"}</div>
                      {cand.bio && (
                        <div className="theme-row-snippet">
                          Current: <em>{cand.bio}</em>
                        </div>
                      )}
                    </div>

                    <div className="theme-row-actions" onClick={(e) => e.stopPropagation()}>
                      {/*cand.matchPercentage !== undefined && (
                        <div className="theme-score-badge">
                          <span className="theme-score-num">{cand.matchPercentage}%</span>
                          <span className="theme-score-lbl">Match</span>
                        </div>
                      )*/}
                      <button 
                        type="button" 
                        className="emd-listing-btn primary theme-view-profile-btn"
                        onClick={() => navigate(`/employer/candidate-search-profile/${cand.id}`)}
                      >
                        →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="emd-no-app-state" style={{ padding: "3rem 1rem" }}>
              <div className="emd-empty-icon">{searchQuery.trim() ? "📂" : "⌨️"}</div>
              <div className="emd-empty-title">
                {searchQuery.trim() ? "No matched professionals located" : "Start typing above"}
              </div>
              <div className="emd-empty-text">
                {searchQuery.trim() 
                  ? "We couldn't find matches for that phrase. Try checking your spelling or typing a single keyword." 
                  : "Results will populate dynamically below as you enter details into the search engine field."}
              </div>
            </div>
          )}
        </div>

      </div>
    </EmpLayout>
  );
}